import { vestResolver } from '@hookform/resolvers/vest'
import { ManyLoansView } from '@sec-loans-ui/common/steps/assistance-step/components/ifp-loans-dropdown/components/ManyLoansView'
import { OtherDebtorsSection } from '@sec-loans-ui/common/steps/assistance-step/components/ifp-loans-dropdown/components/OtherDebtorsSection'
import { SingleLoanView } from '@sec-loans-ui/common/steps/assistance-step/components/ifp-loans-dropdown/components/SingleLoanView'
import {
  IFP_MAXIMUM_CHARGE_AMOUNT,
  IFP_MINIMUM_CHARGE_AMOUNT,
} from '@sec-loans-ui/common/steps/assistance-step/components/ifp-loans-dropdown/constants'
import { createFormValidator } from '@sec-loans-ui/common/steps/assistance-step/components/ifp-loans-dropdown/formValidator'
import { LoanDropdown } from '@sec-loans-ui/common/steps/assistance-step/components/LoanDropdown'
import { RootStepType } from '@sec-loans-ui/constants'
import { OnCreatedIfpApplicationData } from '@sec-loans-ui/ifp-loans/types'
import { getOtherDebtors } from '@sec-loans-ui/ifp-loans/utils'
import { getAvailableIfpLoans, getIfpLoanByUuid } from '@sec-loans-ui/utils/helpers'
import { User } from '@sec-loans-ui/wizard/types'
import { assertValue } from '@shared/helpers'
import { ImplicitlySubmittableForm } from '@ui-common/components/ImplicitlySubmittableForm'
import { useWizardForm } from '@ui-common/hooks'
import { ExternalLink } from '@ui-components/actions/ExternalLink'
import { TightSectionContent } from '@ui-components/containers/TightSectionContent'
import { DataSummaryItem } from '@ui-components/form/DataSummary'
import { useEffect } from 'react'
import { FormProvider } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { useNewIfpApplicationMachineLogic } from './useNewIfpApplicationMachineLogic'

export interface IfpLoansDropdownWizardData {
  selectedLoanUuid?: string
}

export const IfpLoansDropdown = ({
  isOpenByDefault,
  onCreatedIfpLoansApplication,
  onGoToApplicationsClick,
  user: { ifpLoans, debtorUuid },
}: {
  isOpenByDefault: boolean
  onCreatedIfpLoansApplication: (data: OnCreatedIfpApplicationData) => void
  onGoToApplicationsClick: () => void
  user: User
}) => {
  const { formatMessage } = useIntl()

  const { stateValue, createdApplicationId, submit, openErrorModal, analyticsService } =
    useNewIfpApplicationMachineLogic({ onGoToApplicationsClick })

  const availableLoans = getAvailableIfpLoans(ifpLoans)

  const firstIfpLoan = availableLoans[0]
  const hasOneLoan = availableLoans.length === 1 && !!firstIfpLoan

  const formMethods = useWizardForm<IfpLoansDropdownWizardData>({
    defaultValues: {
      selectedLoanUuid: hasOneLoan ? firstIfpLoan.uuid : undefined,
    },
    resolver: vestResolver(createFormValidator(formatMessage)),
  })

  const watchSelectedLoanUuid = formMethods.watch('selectedLoanUuid')
  const selectedLoan = watchSelectedLoanUuid ? getIfpLoanByUuid(availableLoans, watchSelectedLoanUuid) : undefined

  useEffect(() => {
    switch (stateValue) {
      case 'submitFailed': {
        openErrorModal()
        break
      }
      case 'submitCompleted': {
        onCreatedIfpLoansApplication({
          applicationId: assertValue(createdApplicationId, 'createdApplicationId'),
          loanUuid: assertValue(selectedLoan?.uuid, 'selectedLoan?.uuid'),
        })
        break
      }
    }
  }, [stateValue, createdApplicationId, onCreatedIfpLoansApplication, openErrorModal, selectedLoan])

  const onSubmit = (validatedData: IfpLoansDropdownWizardData) => {
    const selectedLoanUuid = assertValue(validatedData.selectedLoanUuid, 'validatedData.selectedLoanUuid')
    analyticsService.sendStepCustomEvent(RootStepType.Assistance, 'CreateNewIfpApplication')
    submit({ loanUuid: selectedLoanUuid })
  }

  const otherDebtors = selectedLoan && getOtherDebtors(assertValue(debtorUuid, 'debtorUuid'), selectedLoan)
  const hasCoDebtors = !!otherDebtors && otherDebtors.length > 0

  return (
    <FormProvider {...formMethods}>
      <ImplicitlySubmittableForm onSubmit={formMethods.handleSubmit(onSubmit)}>
        <LoanDropdown
          header={formatMessage({ id: 'loans-assistance-ifp-loans-dropdown-title' })}
          isOpenByDefault={isOpenByDefault}
          testId="ifp-loans-dropdown"
          isLoadingButtonView={stateValue === 'submitting'}
        >
          <TightSectionContent>
            {hasOneLoan ? <SingleLoanView ifpLoan={firstIfpLoan} /> : <ManyLoansView ifpLoans={availableLoans} />}
            {hasCoDebtors && <OtherDebtorsSection otherDebtors={otherDebtors} />}
            <DataSummaryItem
              id={'ifp-loans-price'}
              label={formatMessage({ id: 'loans-assistance-ifp-loans-price-info-label' })}
              value={formatMessage(
                { id: 'ifp-application-price-info-description' },
                {
                  minimumChargeAmount: IFP_MINIMUM_CHARGE_AMOUNT,
                  maximumChargeAmount: IFP_MAXIMUM_CHARGE_AMOUNT,
                  servicePricingLink: (
                    <ExternalLink href={formatMessage({ id: 'common-service-pricing-link' })}>
                      {formatMessage({ id: 'ifp-application-service-pricing-link-label' })}
                    </ExternalLink>
                  ),
                },
              )}
            />
          </TightSectionContent>
        </LoanDropdown>
      </ImplicitlySubmittableForm>
    </FormProvider>
  )
}
